import React from 'react';
import {
    Paper,
    Stack,
    Typography,
    Box,
    Skeleton,
    Chip,
    IconButton,
    Tooltip,
    Divider,
} from '@mui/material';
import { OpenInNew, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

import { useGetOrders } from '../../hooks/orders/useGetOrders';
import { ORDER_STATUS_META } from '../orders/Table/OrderStatusBadge';

dayjs.extend(relativeTime);

/**
 * Recent activity widget for the dashboard home. Pulls the 10 most
 * recent orders and renders them as a compact event feed.
 *
 * Per ROADMAP §11.4 the eventual design is a global feed sourced from
 * a backend AuditLog table; until that ships, recent orders are the
 * highest-signal proxy for "what just happened in the shop." Replace
 * the data source here when the AuditLog endpoint exists.
 */
const RecentActivity = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();

    const params = { per_page: 10, sort_by: 'placed_at', sort_order: 'desc' };
    const { orders, isLoading, refetch } = useGetOrders(params);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {isArabic ? 'النشاط الأخير' : 'Recent activity'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isArabic
                            ? 'آخر 10 طلبات في المتجر — حدّث للحصول على الأحدث.'
                            : 'Last 10 orders in the shop — refresh for the latest.'}
                    </Typography>
                </Box>
                <Tooltip title={isArabic ? 'تحديث' : 'Refresh'}>
                    <IconButton size="small" onClick={() => refetch()}>
                        <Refresh fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {isLoading ? (
                <Stack spacing={1.5}>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} variant="rounded" height={56} />
                    ))}
                </Stack>
            ) : orders.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic ? 'لا يوجد نشاط حديث' : 'No recent activity'}
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider flexItem />}>
                    {orders.map((o) => {
                        const statusMeta = ORDER_STATUS_META?.[o.status];
                        const statusLabel = statusMeta
                            ? (isArabic ? statusMeta.labelAr : statusMeta.labelEn)
                            : o.status;
                        const statusColor = statusMeta?.color || 'default';
                        const customerName = o.customer?.name || o.customer?.email || (isArabic ? 'ضيف' : 'Guest');
                        const placed = o.placed_at || o.created_at;

                        return (
                            <Stack
                                key={o.id}
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ py: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                onClick={() => navigate(`/orders/${o.id}`)}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                                            #{o.number || o.id}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={statusLabel}
                                            color={statusColor}
                                            variant="tonal"
                                            sx={{ fontWeight: 'bold', borderRadius: '6px', height: 20 }}
                                        />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {customerName}
                                        {placed && (
                                            <>
                                                {' · '}
                                                {dayjs(placed).locale(isArabic ? 'ar' : 'en').fromNow()}
                                            </>
                                        )}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/orders/${o.id}`);
                                    }}
                                >
                                    <OpenInNew fontSize="small" />
                                </IconButton>
                            </Stack>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
};

export default RecentActivity;
